import { useCallback } from 'react';
import { PrimaryDataSet } from '../../types';
import { useProjectData } from '../stores/hooks/useProjectData';
import { addComment, updateComment, removeComment } from '../utils/row-exclusion-manager';
import logger from '../utils/logger';

/**
 * 行コメント機能を提供するカスタムフック
 * 
 * DataTableなどのコンポーネントから使用できる行のコメント機能を提供
 */
export const useRowComments = (dataset: PrimaryDataSet | undefined) => {
  const { updateDataset } = useProjectData();
  
  // 行にコメントを追加する
  const handleAddComment = useCallback((rowNumber: number, comment: string) => {
    if (!dataset) {
      logger.warn('[useRowComments] Cannot add comment: no dataset');
      return;
    }
    
    if (dataset.type !== 'primary') {
      logger.warn('[useRowComments] Cannot add comment: not a primary dataset');
      return;
    }
    
    logger.info('[useRowComments] Adding comment', { 
      datasetId: dataset.id, 
      rowNumber,
      comment 
    });
    
    // コメント追加処理を実行
    addComment(dataset, rowNumber, comment);
    
    // ストアを更新（新しいオブジェクト参照を作成して再レンダリングをトリガー）
    updateDataset(dataset.id, () => ({ ...dataset }));
  }, [dataset, updateDataset]);
  
  // コメントを更新する
  const handleUpdateComment = useCallback((rowNumber: number, comment: string) => {
    if (!dataset) {
      logger.warn('[useRowComments] Cannot update comment: no dataset');
      return;
    }
    
    if (dataset.type !== 'primary') {
      logger.warn('[useRowComments] Cannot update comment: not a primary dataset');
      return;
    }
    
    logger.info('[useRowComments] Updating comment', { 
      datasetId: dataset.id, 
      rowNumber,
      comment 
    });
    
    // コメント更新処理を実行
    updateComment(dataset, rowNumber, comment);
    
    // ストアを更新（新しいオブジェクト参照を作成して再レンダリングをトリガー）
    updateDataset(dataset.id, () => ({ ...dataset }));
  }, [dataset, updateDataset]);
  
  // コメントを削除する
  const handleRemoveComment = useCallback((rowNumber: number) => {
    if (!dataset) {
      logger.warn('[useRowComments] Cannot remove comment: no dataset');
      return;
    }
    
    if (dataset.type !== 'primary') {
      logger.warn('[useRowComments] Cannot remove comment: not a primary dataset');
      return;
    }
    
    logger.info('[useRowComments] Removing comment', { 
      datasetId: dataset.id, 
      rowNumber
    });
    
    // コメント削除処理を実行
    removeComment(dataset, rowNumber);
    
    // ストアを更新（新しいオブジェクト参照を作成して再レンダリングをトリガー）
    updateDataset(dataset.id, () => ({ ...dataset }));
  }, [dataset, updateDataset]);
  
  // 行コメントを取得
  const rowComments = dataset?.type === 'primary' && dataset.comments
    ? dataset.comments
    : {};
  
  return {
    handleAddComment,
    handleUpdateComment,
    handleRemoveComment,
    rowComments,
    isCommentsSupported: dataset?.type === 'primary'
  };
};